// Seat / licence capacity + assignment logic for the Admin Portal, as locked by
// docs/revamp/05_PORTAL_DESIGN.md (§3 "Seats & licence", R3-5).
//
// What this module is — and is NOT:
//   • It is the CLIENT-side capacity math + guard layer that drives the display
//     ("seats in use", "free", "at capacity") and refuses obviously-invalid
//     actions (assign past capacity, double-assign, revoke/reassign a seat that
//     isn't held). It keeps the UI honest and gives instant feedback.
//   • It is NOT the authority. Atomic, race-free enforcement happens server-side
//     as an RPC (task 623). Two admins assigning the last seat at once is the
//     DB's job to settle — this guard cannot, and does not try to.
//
// The plan is NEGOTIATED with us — there is no online checkout. `seatsTotal`
// comes from the licence and is read-only here; this module never changes it.
//
// Capacity rule (matches the prototype's "6 of 12" Overview sample): every seat
// row consumes a seat, whether `active` or a `pending` invite — inviting someone
// RESERVES their seat. So seatsUsed = number of seat holders.
//
// Pure TS, no dependencies. State is immutable: every mutator returns a fresh
// `SeatState`; the input is never modified.

import type { Id, License, Seat, SeatStatus } from "./types";

/**
 * The seat ledger for one organisation: the licence's total seats plus the
 * accounts currently holding (or invited to hold) a seat. Mirrors `License` +
 * `Seat[]` from `./types`, kept together so the capacity math is local.
 */
export interface SeatState {
  orgId: Id;
  /** Total seats the licence permits (negotiated; read-only here). */
  seatsTotal: number;
  /** Accounts holding or invited to a seat — each consumes one seat. */
  readonly seats: readonly Seat[];
}

/** Plain capacity snapshot for the "Seats & licence" / Overview display. */
export interface SeatSummary {
  seatsTotal: number;
  seatsUsed: number;
  seatsFree: number;
  atCapacity: boolean;
}

/** Build a seat ledger from a licence and its current seat rows. */
export function createSeatState(
  license: Pick<License, "orgId" | "seatsTotal">,
  seats: readonly Seat[] = [],
): SeatState {
  return {
    orgId: license.orgId,
    seatsTotal: license.seatsTotal,
    seats: [...seats],
  };
}

/** Seats consumed — every holder counts, `active` or `pending` invite alike. */
export function seatsUsed(state: SeatState): number {
  return state.seats.length;
}

/** Seats still available. Never negative, even if over-allocated server-side. */
export function seatsFree(state: SeatState): number {
  return Math.max(0, state.seatsTotal - seatsUsed(state));
}

/** True once every seat is taken (or over-subscribed). Assigning is refused. */
export function atCapacity(state: SeatState): boolean {
  return seatsUsed(state) >= state.seatsTotal;
}

/** One-shot snapshot for display. */
export function seatSummary(state: SeatState): SeatSummary {
  return {
    seatsTotal: state.seatsTotal,
    seatsUsed: seatsUsed(state),
    seatsFree: seatsFree(state),
    atCapacity: atCapacity(state),
  };
}

/** Whether `accountId` currently holds a seat in this org. */
export function isSeated(state: SeatState, accountId: Id): boolean {
  return state.seats.some((seat) => seat.accountId === accountId);
}

/**
 * Assign a seat to an account. Refuses (throws) when the licence is at capacity
 * — there is no seat to give — and when the account already holds one. On
 * success returns a fresh state with the new seat appended.
 *
 * NOTE: this is the client guard only. The atomic check that prevents two
 * admins claiming the last seat at once lives in the RPC (task 623).
 */
export function assignSeat(
  state: SeatState,
  accountId: Id,
  status: SeatStatus = "active",
): SeatState {
  if (isSeated(state, accountId)) {
    throw new Error(`Account ${accountId} already holds a seat.`);
  }
  if (atCapacity(state)) {
    throw new Error(
      `No free seats: ${seatsUsed(state)} of ${state.seatsTotal} in use. ` +
        "Revoke a seat or contact us to raise the licence.",
    );
  }
  const seat: Seat = { orgId: state.orgId, accountId, status };
  return { ...state, seats: [...state.seats, seat] };
}

/**
 * Move a seat from one account to another. Capacity is unchanged, so this is
 * allowed even at full capacity. Refuses (throws) when the source holds no seat
 * or the target already holds one. The new holder inherits the seat's status by
 * default (override with `status`).
 */
export function reassignSeat(
  state: SeatState,
  fromAccountId: Id,
  toAccountId: Id,
  status?: SeatStatus,
): SeatState {
  if (fromAccountId === toAccountId) {
    throw new Error("Cannot reassign a seat to its current holder.");
  }
  if (!isSeated(state, fromAccountId)) {
    throw new Error(`Account ${fromAccountId} holds no seat to reassign.`);
  }
  if (isSeated(state, toAccountId)) {
    throw new Error(`Account ${toAccountId} already holds a seat.`);
  }
  const seats = state.seats.map((seat) =>
    seat.accountId === fromAccountId
      ? { ...seat, accountId: toAccountId, status: status ?? seat.status }
      : seat,
  );
  return { ...state, seats };
}

/**
 * Revoke (free) the seat held by an account. Refuses (throws) when the account
 * holds no seat. Revoking frees capacity immediately. Revoking a seat is a
 * destructive action — the caller (UI) confirms first per the design brief.
 */
export function revokeSeat(state: SeatState, accountId: Id): SeatState {
  if (!isSeated(state, accountId)) {
    throw new Error(`Account ${accountId} holds no seat to revoke.`);
  }
  return {
    ...state,
    seats: state.seats.filter((seat) => seat.accountId !== accountId),
  };
}
